import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import type { Ailment } from "api/schemas/shared/Treatment";
import { useProfileContext } from "contexts/ProfileContext";
import { translatePainLocation } from "utils/translations/translatePainLocation";

export type TreatmentOption = {
  ailment: Ailment;
  hasLateralLocation: boolean;
  label: string;
  value: string;
};

export function useTreatmentOptions(): TreatmentOption[] {
  return [
    {
      ailment: "joint_pain",
      hasLateralLocation: false,
      label: translatePainLocation({ painLocation: "lower_back" }),
      value: "lower_back",
    },
    {
      ailment: "joint_pain",
      hasLateralLocation: true,
      label: translatePainLocation({ painLocation: "elbow" }),
      value: "elbow",
    },
    {
      ailment: "fall_prevention",
      hasLateralLocation: false,
      label: translatePainLocation({ painLocation: "fall_prevention" }),
      value: "fall_prevention",
    },
    {
      ailment: "joint_pain",
      hasLateralLocation: true,
      label: translatePainLocation({ painLocation: "foot" }),
      value: "foot",
    },
    {
      ailment: "joint_pain",
      hasLateralLocation: true,
      label: translatePainLocation({ painLocation: "hand" }),
      value: "hand",
    },
    {
      ailment: "joint_pain",
      hasLateralLocation: true,
      label: translatePainLocation({ painLocation: "hip" }),
      value: "hip",
    },
    {
      ailment: "joint_pain",
      hasLateralLocation: true,
      label: translatePainLocation({ painLocation: "knee" }),
      value: "knee",
    },
    {
      ailment: "joint_pain",
      hasLateralLocation: true,
      label: translatePainLocation({ painLocation: "lower_leg" }),
      value: "lower_leg",
    },
    {
      ailment: "joint_pain",
      hasLateralLocation: false,
      label: translatePainLocation({ painLocation: "neck" }),
      value: "neck",
    },
    {
      ailment: "osteoporosis",
      hasLateralLocation: false,
      label: translatePainLocation({ painLocation: "osteoporosis" }),
      value: "osteoporosis",
    },
    {
      ailment: "joint_pain",
      hasLateralLocation: true,
      label: translatePainLocation({ painLocation: "shoulder" }),
      value: "shoulder",
    },
    {
      ailment: "joint_pain",
      hasLateralLocation: true,
      label: translatePainLocation({ painLocation: "wrist" }),
      value: "wrist",
    },
  ];
}

export type Option<T> = {
  label: string;
  value: T;
};

export function useTreatmentTypeOptions(): Option<string>[] {
  const { t } = useTranslation();
  const { profile } = useProfileContext();

  const treatmentTypeOptions = useMemo(() => {
    const showHEP = profile?.therapist_profile?.branded_health_care_provider?.feature_flags?.includes("HEP_ENABLED");
    const showRTM = profile?.therapist_profile?.branded_health_care_provider?.feature_flags?.includes("RTM_ENABLED");
    const showDigital = profile?.therapist_profile?.feature_flags?.includes("HYBRID_TREATMENT_ENABLED");
    const showHybrid =
      profile?.therapist_profile?.branded_health_care_provider?.feature_flags?.includes("HYBRID_ENABLED") ||
      showDigital;

    const options: Option<string>[] = [];

    if (showHEP) {
      options.push({ label: t("invite.treatment_type_options.hep"), value: "hep" });
    }

    if (showRTM) {
      options.push({ label: t("invite.treatment_type_options.rtm"), value: "rtm" });
    }

    if (showHybrid) {
      options.push({ label: t("invite.treatment_type_options.hybrid"), value: "hybrid" });
    }

    if (showDigital) {
      options.push({ label: t("invite.treatment_type_options.digital"), value: "digital" });
    }

    return options;
  }, [profile, t]);

  return treatmentTypeOptions;
}
